import { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Role hierarchy for client-side checks
export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ORG_ADMIN: 'ORG_ADMIN',
  ORG_USER: 'ORG_USER',
  ORG_VIEWER: 'ORG_VIEWER',
  CUSTOMER_USER: 'CUSTOMER_USER'
};

export const ROLE_LEVELS = {
  [ROLES.SUPER_ADMIN]: 0,
  [ROLES.ORG_ADMIN]: 1,
  [ROLES.ORG_USER]: 2,
  [ROLES.ORG_VIEWER]: 3,
  [ROLES.CUSTOMER_USER]: 4
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Try to refresh user from backend
      fetch(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${storedToken}`
        }
      })
        .then(async (res) => {
          if (!res.ok) {
            // Token expired or invalid - logout
            if (res.status === 401) {
              logout();
            }
            return;
          }
          const data = await res.json();
          // Also fetch subscription plan from /subscriptions/info
          let subscriptionPlan = data.subscriptionPlan;
          if (!subscriptionPlan) {
            try {
              const subRes = await fetch(`${API_BASE_URL}/subscriptions/info`, {
                headers: { Authorization: `Bearer ${storedToken}` }
              });
              if (subRes.ok) {
                const subData = await subRes.json();
                subscriptionPlan = subData.organization?.subscriptionPlan;
              }
            } catch {}
          }
          const userData = { 
            id: data.id, 
            email: data.email, 
            role: data.role,
            firstName: data.firstName,
            lastName: data.lastName,
            avatar: data.avatar,
            organizationId: data.organizationId,
            organizationName: data.organizationName,
            subscriptionPlan: subscriptionPlan
          };
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        })
        .catch(() => {});
    }
    setLoading(false);
  }, []);

  const parseErrorResponse = async (response) => {
    try {
      const json = await response.json();
      return json?.message || JSON.stringify(json);
    } catch (_) {
      try {
        const text = await response.text();
        return text || `HTTP ${response.status}`;
      } catch (__) {
        return `HTTP ${response.status}`;
      }
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ email, password, rememberMe })
      });

      if (!response.ok) {
        const message = await parseErrorResponse(response);
        throw new Error(message || 'Login failed');
      }

      const data = await response.json();
      
      setToken(data.access_token);
      setUser(data.user);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (formData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const message = await parseErrorResponse(response);
        throw new Error(message || 'Registration failed');
      }

      const data = await response.json();
      
      // Check if registration requires approval (new org admins)
      if (data.requiresApproval) {
        return { success: true, requiresApproval: true, message: data.message };
      }
      
      // Direct login if no approval needed
      if (data.access_token) {
        setToken(data.access_token);
        setUser(data.user);
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
      }
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Keep rememberMe settings
  };

  const isAuthenticated = () => {
    return !!token;
  };

  /**
   * Check if user has at least the required role level
   * Lower level = higher privilege
   */
  const hasRole = (requiredRole) => {
    if (!user?.role) return false;
    const userLevel = ROLE_LEVELS[user.role] ?? 999;
    const requiredLevel = ROLE_LEVELS[requiredRole] ?? 0;
    return userLevel <= requiredLevel;
  };

  /**
   * Check if user has exact role
   */
  const isRole = (role) => {
    return user?.role === role;
  };

  /**
   * Check if user is SUPER_ADMIN
   */
  const isSuperAdmin = () => isRole(ROLES.SUPER_ADMIN);

  /**
   * Check if user is at least ORG_ADMIN
   */
  const isOrgAdmin = () => hasRole(ROLES.ORG_ADMIN);

  /**
   * Check if user is at least ORG_USER
   */
  const isOrgUser = () => hasRole(ROLES.ORG_USER);

  /**
   * Check if user can only view (ORG_VIEWER or CUSTOMER_USER)
   */
  const isViewOnly = () => {
    return user?.role === ROLES.ORG_VIEWER || user?.role === ROLES.CUSTOMER_USER;
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    isAuthenticated,
    loading,
    // Role helpers
    hasRole,
    isRole,
    isSuperAdmin,
    isOrgAdmin,
    isOrgUser,
    isViewOnly,
    ROLES
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
